'use strict'

// Conditions and display actions for formatting rules on a view field.
// The designer, the live form, and the app server all use this file.

const OPERATORS = {
  number: [
    { key: '=', label: 'is equal to' },
    { key: '!=', label: 'is not equal to' },
    { key: '>', label: 'greater than' },
    { key: '>=', label: 'greater than or equal to' },
    { key: '<', label: 'less than' },
    { key: '<=', label: 'less than or equal to' },
    { key: 'empty', label: 'is blank' },
    { key: 'notEmpty', label: 'is not blank' }
  ],
  string: [
    { key: '=', label: 'is equal to' },
    { key: '!=', label: 'is not equal to' },
    { key: 'matches', label: 'matches regexp' },
    { key: 'notMatches', label: 'does not match regexp' },
    { key: 'contains', label: 'contains' },
    { key: 'notContains', label: 'does not contain' },
    { key: 'begins', label: 'starts with' },
    { key: 'ends', label: 'ends with' },
    { key: 'empty', label: 'is blank' },
    { key: 'notEmpty', label: 'is not blank' }
  ],
  datetime: [
    { key: '=', label: 'is equal to' },
    { key: '!=', label: 'is not equal to' },
    { key: 'before', label: 'is before' },
    { key: 'after', label: 'is after' },
    { key: 'empty', label: 'is blank' },
    { key: 'notEmpty', label: 'is not blank' }
  ],
  relationship: [
    { key: '=', label: 'is equal to' },
    { key: '!=', label: 'is not equal to' },
    { key: 'contains', label: 'contains' },
    { key: 'notContains', label: 'does not contain' },
    { key: 'empty', label: 'is blank' },
    { key: 'notEmpty', label: 'is not blank' }
  ],
  checkbox: [
    { key: '=', label: 'is equal to' },
    { key: '!=', label: 'is not equal to' },
    { key: 'empty', label: 'is blank' },
    { key: 'notEmpty', label: 'is not blank' }
  ],
  signature: [
    { key: 'empty', label: 'is blank' },
    { key: 'notEmpty', label: 'is not blank' }
  ]
}

const FONT_OPTIONS = [
  { value: '', label: 'Default' },
  { value: 'Arial, sans-serif', label: 'Arial' },
  { value: 'Georgia, serif', label: 'Georgia' },
  { value: "'Times New Roman', serif", label: 'Times New Roman' },
  { value: 'Verdana, sans-serif', label: 'Verdana' },
  { value: "'Courier New', monospace", label: 'Courier New' },
  { value: 'system-ui, sans-serif', label: 'System' }
]

const DISPLAY_ACTIONS = [
  { key: 'bold', label: 'Bold', type: 'boolean', default: true },
  { key: 'italic', label: 'Italic', type: 'boolean', default: true },
  { key: 'underline', label: 'Underline', type: 'boolean', default: true },
  { key: 'strikethrough', label: 'Strikethrough', type: 'boolean', default: true },
  { key: 'fontFamily', label: 'Font', type: 'select', default: '', options: FONT_OPTIONS },
  { key: 'color', label: 'Text Color', type: 'color', default: '#ff0000' },
  { key: 'bgColor', label: 'Background Color', type: 'color', default: '#333333' },
  { key: 'borderColor', label: 'Border Color', type: 'color', default: '#0000ff' },
  { key: 'borderWidth', label: 'Border Width (px)', type: 'number', default: 2 },
  { key: 'fontSize', label: 'Font Size (px)', type: 'number', default: 14 },
  { key: 'hidden', label: 'Hide Value', type: 'boolean', default: true },
  { key: 'customCss', label: 'Custom CSS', type: 'string', default: '' }
]

function operatorsForDataType (dataType) {

  return OPERATORS[dataType] || OPERATORS.string
}

function isBlankOperator (operator) {

  return operator === 'empty' || operator === 'notEmpty'
}

function formattingPropertiesByKey () {

  const map = {}

  for (const action of DISPLAY_ACTIONS) {

    map[action.key] = {
      type: action.type,
      label: action.label,
      default: action.default
    }
  }

  return map
}

function isBlankValue (value) {

  if (Array.isArray(value)) return value.length === 0

  if (typeof Buffer !== 'undefined' && Buffer.isBuffer(value)) return value.length === 0

  if (value && value.type === 'Buffer' && Array.isArray(value.data)) return value.data.length === 0

  return value === null || value === undefined || value === ''
}

function relationshipIds (value) {

  const list = Array.isArray(value)
    ? value
    : (value === null || value === undefined || value === '' ? [] : [value])

  return list.map((item) => {

    if (item && typeof item === 'object' && item.id != null) return String(item.id)

    return String(item)
  }).filter((id) => id !== '')
}

function sameIdSet (left, right) {

  if (left.length !== right.length) return false

  const a = left.slice().sort()
  const b = right.slice().sort()

  return a.every((id, index) => id === b[index])
}

function regexpMatches (testValue, pattern, wantMatch) {

  if (testValue == null) return false

  let expression

  try {

    expression = new RegExp(String(pattern))
  } catch (err) {

    return false
  }

  const matched = expression.test(String(testValue))

  return wantMatch ? matched : !matched
}

function compareValues (testValue, operator, conditionValue) {

  switch (operator) {

    case '=':
      return testValue == conditionValue // eslint-disable-line eqeqeq

    case '!=':
      return testValue != conditionValue // eslint-disable-line eqeqeq

    case '>':
      return testValue > conditionValue

    case '<':
      return testValue < conditionValue

    case '>=':
      return testValue >= conditionValue

    case '<=':
      return testValue <= conditionValue

    case 'contains':
      return testValue != null && String(testValue).toLowerCase().includes(String(conditionValue).toLowerCase())

    case 'notContains':
      return testValue == null || !String(testValue).toLowerCase().includes(String(conditionValue).toLowerCase())

    case 'begins':
      return testValue != null && String(testValue).toLowerCase().startsWith(String(conditionValue).toLowerCase())

    case 'ends':
      return testValue != null && String(testValue).toLowerCase().endsWith(String(conditionValue).toLowerCase())

    case 'matches':
      return regexpMatches(testValue, conditionValue, true)

    case 'notMatches':
      return regexpMatches(testValue, conditionValue, false)

    case 'empty':
      return isBlankValue(testValue)

    case 'notEmpty':
      return !isBlankValue(testValue)

    case 'before':
      return new Date(testValue) < new Date(conditionValue)

    case 'after':
      return new Date(testValue) > new Date(conditionValue)

    default:
      return testValue == conditionValue // eslint-disable-line eqeqeq
  }
}

function matchesDisplayCondition (testValue, operator, conditionValue, dataType) {

  if (dataType === 'relationship' && (operator === '=' || operator === '!=' || operator === 'contains' || operator === 'notContains')) {

    const selected = relationshipIds(conditionValue)
    const current = relationshipIds(testValue)

    if (operator === 'contains') return selected.every((id) => current.includes(id))

    if (operator === 'notContains') return !selected.every((id) => current.includes(id))

    const equal = sameIdSet(selected, current)

    return operator === '=' ? equal : !equal
  }

  return compareValues(testValue, operator, conditionValue)
}

function formattingToStyle (fmt) {

  if (!fmt) return {}

  const style = {}

  if (fmt.color) style.color = fmt.color
  if (fmt.bgColor) style.backgroundColor = fmt.bgColor
  if (fmt.bold) style.fontWeight = 'bold'
  if (fmt.italic) style.fontStyle = 'italic'
  if (fmt.fontSize) style.fontSize = fmt.fontSize + 'px'
  if (fmt.fontFamily) style.fontFamily = fmt.fontFamily
  if (fmt.borderColor) {
    style.border = `${fmt.borderWidth || 1}px solid ${fmt.borderColor}`
  }

  const decorations = []

  if (fmt.underline) decorations.push('underline')
  if (fmt.strikethrough) decorations.push('line-through')
  if (decorations.length) style.textDecoration = decorations.join(' ')

  if (fmt.customCss) {

    const parts = String(fmt.customCss).split(';')

    for (const part of parts) {

      const pieces = part.split(':')
      const prop = pieces.shift()
      const val = pieces.join(':')

      if (!prop || !val) continue

      const name = prop.trim()
      const value = val.trim()

      if (!name || !value) continue

      const camel = name.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase())

      style[camel] = value
    }
  }

  return style
}

function formattingToStyleString (fmt) {

  const style = formattingToStyle(fmt)
  const parts = []

  for (const key of Object.keys(style)) {

    const cssName = key.replace(/[A-Z]/g, (letter) => '-' + letter.toLowerCase())

    parts.push(`${cssName}:${style[key]}`)
  }

  return parts.join(';')
}

module.exports = {
  OPERATORS,
  DISPLAY_ACTIONS,
  operatorsForDataType,
  isBlankOperator,
  formattingPropertiesByKey,
  matchesDisplayCondition,
  formattingToStyle,
  formattingToStyleString
}
